/**
 * Storage shim — Supabase-backed.
 *
 * Mirrors the same `window.storage.get/set/delete/list` async API the app
 * was already using. Routes calls to Supabase tables:
 *
 *   - tp_plan_state_v1        → plans (state_data JSONB column for active plan)
 *   - tp_plan_registry_shared → derived from a SELECT of plans rows
 *   - tp_tool_feedback_shared → feedback (one row per item, content JSONB)
 *
 * The "active plan" id is tracked in this module. Call setActivePlan(id)
 * before reading/writing per-plan state so calls target the right row.
 *
 * Local browser remembers the active plan id across reloads via localStorage,
 * but no plan content is ever stored locally.
 */

import { supabase, isSupabaseConfigured } from "./supabase.js";
import { computePlanWithSavings } from "./compute.js";

export const KEYS = {
  PLAN_STATE: "tp_plan_state_v1",
  PLAN_REGISTRY: "tp_plan_registry_shared",
  FEEDBACK: "tp_tool_feedback_shared",
};

const ACTIVE_PLAN_KEY = "tp_active_plan_id";
let currentPlanId = null;

if (typeof window !== "undefined") {
  try {
    const saved = localStorage.getItem(ACTIVE_PLAN_KEY);
    if (saved) currentPlanId = saved;
  } catch (e) {
    /* ignore */
  }
}

export function setActivePlan(id) {
  currentPlanId = id || null;
  if (typeof window !== "undefined") {
    try {
      if (id) localStorage.setItem(ACTIVE_PLAN_KEY, id);
      else localStorage.removeItem(ACTIVE_PLAN_KEY);
    } catch (e) {
      /* ignore */
    }
  }
}

export function getActivePlan() {
  return currentPlanId;
}

// Compute denormalized fields from a parsed plan state for the
// home-screen list. Savings is computed via the same engine the UI uses.
function denormalize(state) {
  let savings = 0;
  try {
    const r = computePlanWithSavings(state);
    savings = Math.max(0, Math.round(r.savings || 0));
  } catch (e) {
    /* compute errors swallowed; savings stays 0 */
  }
  return {
    client_name: state.clientName || null,
    tax_year: parseInt(state.taxYear, 10) || null,
    state_code: state.state || null,
    stage: state.lifecycle?.stage || "prospect",
    entity_count: Array.isArray(state.entities) ? state.entities.length : 0,
    savings,
  };
}

async function get(key, shared = false) {
  if (!isSupabaseConfigured) return null;
  try {
    if (key === KEYS.PLAN_REGISTRY) {
      const { data, error } = await supabase
        .from("plans")
        .select(
          "id, client_name, tax_year, state_code, stage, entity_count, savings, created_at, updated_at",
        )
        .order("updated_at", { ascending: false });
      if (error) throw error;
      const items = (data || []).map((p) => ({
        id: p.id,
        clientName: p.client_name,
        taxYear: p.tax_year,
        state: p.state_code,
        stage: p.stage,
        entityCount: p.entity_count,
        savings: p.savings,
        createdAt: p.created_at,
        updatedAt: p.updated_at,
      }));
      return { key, value: JSON.stringify(items), shared: true };
    }

    if (key === KEYS.PLAN_STATE) {
      if (!currentPlanId) return null;
      const { data, error } = await supabase
        .from("plans")
        .select("state_data")
        .eq("id", currentPlanId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return { key, value: JSON.stringify(data.state_data || {}), shared: false };
    }

    if (key === KEYS.FEEDBACK) {
      const { data, error } = await supabase
        .from("feedback")
        .select("content")
        .order("created_at", { ascending: true });
      if (error) throw error;
      const items = (data || []).map((row) => row.content);
      return { key, value: JSON.stringify(items), shared: true };
    }

    return null;
  } catch (e) {
    console.error("storage.get error", e);
    return null;
  }
}

async function set(key, value, shared = false) {
  if (!isSupabaseConfigured) return null;
  try {
    if (key === KEYS.PLAN_REGISTRY) {
      // No-op — registry is derived from plans rows on each get.
      return { key, value, shared };
    }

    if (key === KEYS.PLAN_STATE) {
      if (!currentPlanId) {
        console.error("storage.set: setActivePlan(id) must be called first");
        return null;
      }
      const stateObj = JSON.parse(value);
      const denorm = denormalize(stateObj);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { error } = await supabase.from("plans").upsert({
        id: currentPlanId,
        ...denorm,
        state_data: stateObj,
        last_edited_by: user?.id || null,
        updated_at: new Date().toISOString(),
      });
      if (error) throw error;
      return { key, value, shared };
    }

    if (key === KEYS.FEEDBACK) {
      // Upsert each item by id so adding responses to an existing item
      // (which doesn't change id) is also synced back to the DB.
      const newArr = JSON.parse(value);
      if (!Array.isArray(newArr) || newArr.length === 0) {
        return { key, value, shared };
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const rows = newArr.map((item) => ({
        id: item.id,
        content: item,
        author_id: user?.id || null,
      }));
      const { error } = await supabase.from("feedback").upsert(rows);
      if (error) throw error;
      return { key, value, shared };
    }

    return null;
  } catch (e) {
    console.error("storage.set error", e);
    return null;
  }
}

async function del(key, shared = false) {
  if (!isSupabaseConfigured) return null;
  try {
    if (key === KEYS.PLAN_STATE) {
      if (!currentPlanId) return null;
      const { error } = await supabase
        .from("plans")
        .delete()
        .eq("id", currentPlanId);
      if (error) throw error;
      return { key, deleted: true, shared };
    }
    return null;
  } catch (e) {
    console.error("storage.delete error", e);
    return null;
  }
}

async function list() {
  return { keys: [], prefix: "", shared: false };
}

export async function getPlanStateById(planId) {
  if (!isSupabaseConfigured) return null;
  try {
    const { data, error } = await supabase
      .from("plans")
      .select("state_data")
      .eq("id", planId)
      .maybeSingle();
    if (error) throw error;
    return data?.state_data || null;
  } catch (e) {
    return null;
  }
}

export async function deletePlanStateById(planId) {
  if (!isSupabaseConfigured) return false;
  try {
    const { error } = await supabase.from("plans").delete().eq("id", planId);
    if (error) throw error;
    return true;
  } catch (e) {
    return false;
  }
}

// ─── Custom strategies (firm-wide, shared) ──────────────────────────────
//
// Approved firm users can create, edit, and delete custom strategies that
// extend the seeded library. Stored in custom_strategies (id text + content
// JSONB) so the schema can absorb future strategy-shape changes.

export async function loadCustomStrategies() {
  if (!isSupabaseConfigured) return [];
  try {
    const { data, error } = await supabase
      .from("custom_strategies")
      .select("id, content")
      .order("created_at", { ascending: true });
    if (error) throw error;
    return (data || []).map((row) => row.content).filter(Boolean);
  } catch (e) {
    console.error("loadCustomStrategies error", e);
    return [];
  }
}

export async function saveCustomStrategy(strategy) {
  if (!isSupabaseConfigured || !strategy?.id) return null;
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const { error } = await supabase.from("custom_strategies").upsert({
      id: strategy.id,
      content: strategy,
      author_id: user?.id || null,
    });
    if (error) throw error;
    return strategy;
  } catch (e) {
    console.error("saveCustomStrategy error", e);
    return null;
  }
}

export async function deleteCustomStrategy(id) {
  if (!isSupabaseConfigured || !id) return false;
  try {
    const { error } = await supabase
      .from("custom_strategies")
      .delete()
      .eq("id", id);
    if (error) throw error;
    return true;
  } catch (e) {
    console.error("deleteCustomStrategy error", e);
    return false;
  }
}

// ─── File attachments (Supabase Storage) ────────────────────────────────
//
// Excel docs, PDFs, and other files attached to entities. Stored in a
// private bucket `plan-attachments` with RLS allowing only approved
// users. Path layout:
//   plans/{planId}/entities/{entityId}/{fileId}-{filename}

const ATTACHMENTS_BUCKET = "plan-attachments";

function sanitizeFilename(name) {
  return String(name || "file")
    .replace(/[^\w.\-]+/g, "_")
    .slice(0, 120);
}

/**
 * Generic uploader. hostKey is a path segment used to organize files —
 * "entities/{entityId}" or "individual" for the 1040.
 */
export async function uploadAttachment(planId, hostKey, file) {
  if (!isSupabaseConfigured) return null;
  if (!planId || !hostKey || !file) return null;
  try {
    const fileId = `${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 7)}`;
    const safeName = sanitizeFilename(file.name);
    const path = `plans/${planId}/${hostKey}/${fileId}-${safeName}`;
    const { data, error } = await supabase.storage
      .from(ATTACHMENTS_BUCKET)
      .upload(path, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type || "application/octet-stream",
      });
    if (error) throw error;
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return {
      id: fileId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || "",
      storagePath: data.path,
      uploadedAt: new Date().toISOString(),
      uploadedBy: user?.email || user?.id || null,
    };
  } catch (e) {
    console.error("uploadAttachment error", e);
    throw e;
  }
}

// Backward-compat wrapper — existing call sites pass (planId, entityId, file)
export async function uploadEntityAttachment(planId, entityId, file) {
  return uploadAttachment(planId, `entities/${entityId}`, file);
}

export async function getAttachmentDownloadUrl(storagePath, fileName) {
  if (!isSupabaseConfigured || !storagePath) return null;
  try {
    const { data, error } = await supabase.storage
      .from(ATTACHMENTS_BUCKET)
      .createSignedUrl(storagePath, 3600, fileName ? { download: fileName } : undefined);
    if (error) throw error;
    return data?.signedUrl || null;
  } catch (e) {
    console.error("getAttachmentDownloadUrl error", e);
    return null;
  }
}

// ─── Strategy event tracking ─────────────────────────────────────────────
//
// Fire-and-forget logging for partner engagement signals:
//   brief_opened    — Strategy Brief popup opened
//   strategy_added  — strategy selected/added to a plan
//   strategy_removed — strategy removed from a plan
//
// Surfaces in the Analytics dashboard so the firm can see what's getting
// clicked vs what's actually being applied to client plans.

export async function logStrategyEvent({
  strategyId,
  eventType,
  source = null,
  planId = null,
}) {
  if (!isSupabaseConfigured || !strategyId || !eventType) return null;
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    // Insert is fire-and-forget — never await UI-blocking
    await supabase.from("strategy_events").insert({
      strategy_id: strategyId,
      event_type: eventType,
      source,
      plan_id: planId,
      user_id: user?.id || null,
      user_email: user?.email || null,
    });
  } catch (e) {
    // Swallow errors — analytics logging must never break the UI
    console.warn("logStrategyEvent error", e);
  }
}

export async function loadStrategyEvents(daysBack = 90) {
  if (!isSupabaseConfigured) return [];
  try {
    const since = new Date(
      Date.now() - daysBack * 24 * 60 * 60 * 1000,
    ).toISOString();
    const { data, error } = await supabase
      .from("strategy_events")
      .select("strategy_id, event_type, source, user_email, plan_id, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (e) {
    console.error("loadStrategyEvents error", e);
    return [];
  }
}

// ─── Analytics — fetch all plans with full state for aggregation ────────
//
// Pulls every plan the signed-in user can see (RLS already constrains to
// approved firm users) so the dashboard can compute strategy counts,
// pipeline funnels, savings totals, and activity over time.
//
// For a firm at hundreds of plans this stays fast. At thousands, move to
// a Supabase view / RPC that aggregates server-side.
export async function loadAllPlansForAnalytics() {
  if (!isSupabaseConfigured) return [];
  try {
    const { data, error } = await supabase
      .from("plans")
      .select(
        "id, client_name, tax_year, state_code, stage, entity_count, savings, state_data, created_at, updated_at, last_edited_by",
      )
      .order("updated_at", { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (e) {
    console.error("loadAllPlansForAnalytics error", e);
    return [];
  }
}

export async function deleteAttachment(storagePath) {
  if (!isSupabaseConfigured || !storagePath) return false;
  try {
    const { error } = await supabase.storage
      .from(ATTACHMENTS_BUCKET)
      .remove([storagePath]);
    if (error) throw error;
    return true;
  } catch (e) {
    console.error("deleteAttachment error", e);
    return false;
  }
}

if (typeof window !== "undefined") {
  window.storage = { get, set, delete: del, list };
}

export default {
  get,
  set,
  delete: del,
  list,
  setActivePlan,
  getActivePlan,
  getPlanStateById,
  deletePlanStateById,
  KEYS,
};
